import { useMemo, useState, type FormEvent } from "react";
import { useQueries } from "@tanstack/react-query";
import { Link, Navigate, useNavigate, useParams, useSearchParams } from "react-router";
import {
  DEFAULT_WRITING_RULES,
  isAuthorStory,
  type AuthorStory,
  type CreateVersionBody,
  type PublicStory,
  type StoryVersion,
  type VersionLearningSetup,
  type WritingRules,
} from "@storyai/shared";
import { WritingRulesEditor } from "../components/learning/WritingRulesEditor";
import { SessionRow } from "../components/sessions";
import {
  Badge,
  EmptyState,
  ErrorPanel,
  InlineConfirm,
  Loading,
  MutationError,
  PageHeader,
  VisibilityBadge,
} from "../components/ui";
import { sessionsApi } from "../lib/api";
import {
  keys,
  useCreateChapter,
  useCreateSession,
  useReorderChapters,
  useStory,
  useVersionMutations,
  useVersions,
} from "../lib/queries";
import {
  mergeSessionLists,
  pickInitialChoice,
  versionChoices,
  versionStatusLabel,
  type VersionChoice,
} from "../lib/versions";

export function StoryLandingPage() {
  const { storyId = "" } = useParams();
  const q = useStory(storyId);

  if (q.isPending) return <Loading />;
  if (q.isError) return <ErrorPanel error={q.error} />;

  const { story, isAuthor } = q.data;
  const author = isAuthor && isAuthorStory(story) ? story : null;

  // Players never land on a version's own page: send them to the base with
  // that version preselected. Authors can open a version directly.
  if (story.versionOf && !author) {
    return <Navigate to={`/stories/${story.versionOf.baseStoryId}?version=${story.versionOf.versionId}`} replace />;
  }

  return (
    <div className="page">
      <PageHeader
        crumbs={[
          { to: author ? "/my-stories" : "/stories", label: author ? "My Stories" : "Public Stories" },
          ...(story.versionOf
            ? [{ to: `/stories/${story.versionOf.baseStoryId}`, label: story.versionOf.baseTitle }]
            : []),
          { label: story.versionOf ? story.versionOf.title : story.title },
        ]}
        title={story.title}
        subtitle={<>by {story.author.displayName}</>}
        actions={author && <AuthorToolbar story={author} />}
      />

      {story.versionOf && (
        <div className="version-banner">
          Version <strong>{story.versionOf.title}</strong> of{" "}
          <Link to={`/stories/${story.versionOf.baseStoryId}`}>{story.versionOf.baseTitle}</Link>
        </div>
      )}

      <div className="story-hero">
        {story.coverUrl ? (
          <img className="story-cover" src={story.coverUrl} alt="" />
        ) : (
          <div className="story-cover cover-placeholder" aria-hidden="true">
            {story.title.slice(0, 1).toUpperCase()}
          </div>
        )}
        <div className="story-hero-body">
          <div className="story-card-meta">
            {story.rating && <Badge tone="muted">{story.rating}</Badge>}
            {story.tags.map((t) => (
              <span key={t} className="tag">
                {t}
              </span>
            ))}
          </div>
          {story.blurb ? (
            <p className="story-blurb">{story.blurb}</p>
          ) : (
            <p className="muted">No description yet.</p>
          )}
          <StartSession story={story} noStartChapter={author !== null && !author.startChapterId} />
          <div className="muted small">
            {story.chapterCount} {story.chapterCount === 1 ? "chapter" : "chapters"}
          </div>
        </div>
      </div>

      <SessionsSection story={story} />

      {author && !author.versionOf && <VersionsSection story={author} />}
      {author && <ChaptersSection story={author} />}
    </div>
  );
}

// ---- Start + version chooser ---------------------------------------------------

function StartSession({ story, noStartChapter }: { story: PublicStory | AuthorStory; noStartChapter: boolean }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // Players only ever receive enabled+ready versions; authors get every
  // version, so drop the ones that aren't playable yet.
  const choices = useMemo(
    () =>
      versionChoices(
        story.id,
        story.versions.filter((v) => !("status" in v) || ((v as StoryVersion).status === "ready" && (v as StoryVersion).enabled)),
      ),
    [story.id, story.versions],
  );
  const [choice, setChoice] = useState<VersionChoice>(() => pickInitialChoice(choices, params.get("version")));
  const create = useCreateSession(choice.storyId);
  const hasVersions = choices.length > 1 && !story.versionOf;

  async function start() {
    try {
      const res = await create.mutateAsync(undefined);
      navigate(`/stories/${choice.storyId}/sessions/${res.session.session.id}`);
    } catch {
      /* error rendered below */
    }
  }

  return (
    <div className="story-play">
      {hasVersions && (
        <fieldset className="version-chooser">
          <legend className="muted small">Choose a version</legend>
          {choices.map((c) => {
            const id = `version-${c.versionId ?? "original"}`;
            const checked = choice.storyId === c.storyId;
            return (
              <label key={id} htmlFor={id} className={checked ? "version-choice selected" : "version-choice"}>
                <input
                  id={id}
                  type="radio"
                  name="version"
                  checked={checked}
                  onChange={() => setChoice(c)}
                />
                <span className="version-choice-body">
                  <span className="version-choice-title">
                    {c.title}
                    {c.language && <span className="tag">{c.language}</span>}
                    {c.learning && <span className="tag tag-learning">Learning</span>}
                  </span>
                  {c.description && <span className="muted small">{c.description}</span>}
                </span>
              </label>
            );
          })}
        </fieldset>
      )}
      <div className="story-play-row">
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => void start()}
          disabled={noStartChapter || create.isPending}
        >
          {create.isPending ? "Starting…" : "Start new session"}
        </button>
        {noStartChapter && <span className="muted small">Add a chapter first.</span>}
      </div>
      <MutationError error={create.error} />
    </div>
  );
}

// ---- Sessions across the base and its versions --------------------------------

function SessionsSection({ story }: { story: PublicStory | AuthorStory }) {
  const targets = useMemo(
    () => [
      { storyId: story.id, versionTitle: null as string | null },
      ...story.versions.map((v) => ({ storyId: v.storyId, versionTitle: v.title as string | null })),
    ],
    [story.id, story.versions],
  );
  const results = useQueries({
    queries: targets.map((t) => ({
      queryKey: keys.storySessions(t.storyId),
      queryFn: () => sessionsApi.listForStory(t.storyId),
      retry: false,
    })),
  });
  const pending = results.some((r) => r.isPending);
  const error = results.find((r) => r.isError)?.error;
  const sessions = useMemo(
    () =>
      mergeSessionLists(
        results.map((r, i) => ({ versionTitle: targets[i]!.versionTitle, sessions: r.data?.sessions ?? [] })),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [results.map((r) => r.dataUpdatedAt).join("|"), targets],
  );

  return (
    <section className="section">
      <h2>Your sessions</h2>
      {pending ? (
        <Loading />
      ) : error ? (
        <ErrorPanel error={error} />
      ) : sessions.length === 0 ? (
        <EmptyState title="No sessions yet.">
          Start a new session above. You can have several going at once, one per branch you
          want to explore.
        </EmptyState>
      ) : (
        <ul className="session-list">
          {sessions.map((s) => (
            <SessionRow key={s.id} session={s} />
          ))}
        </ul>
      )}
    </section>
  );
}

// ---- Author toolbar ------------------------------------------------------------------

function AuthorToolbar({ story }: { story: AuthorStory }) {
  const [copied, setCopied] = useState(false);
  const canShare = story.visibility !== "private";

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/stories/${story.id}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* clipboard unavailable; nothing to do */
    }
  }

  return (
    <div className="toolbar">
      {!story.versionOf && <VisibilityBadge visibility={story.visibility} />}
      {canShare && !story.versionOf && (
        <button type="button" className="btn btn-sm" onClick={copyLink}>
          {copied ? "Copied" : "Copy link"}
        </button>
      )}
      <Link to={`/stories/${story.id}/map`} className="btn btn-sm btn-primary">
        Map
      </Link>
      <Link to={`/stories/${story.id}/map`} className="btn btn-sm">
        Edit
      </Link>
    </div>
  );
}

// ---- Versions (author, base story only) -----------------------------------------

const TRANSFORM_HINT =
  "Leave blank to copy as-is. Examples: “Translate everything into Spanish, preserving tone.” " +
  "“Rewrite for an 8th-grade reading level.”";

function VersionsSection({ story }: { story: AuthorStory }) {
  const versionsQ = useVersions(story.id);
  const mut = useVersionMutations(story.id);
  const [showForm, setShowForm] = useState(false);
  const versions = versionsQ.data?.versions ?? story.versions;

  return (
    <section className="section">
      <div className="section-head">
        <h2>Versions</h2>
        <button type="button" className="btn btn-sm btn-primary" onClick={() => setShowForm((v) => !v)}>
          {showForm ? "Cancel" : "New version"}
        </button>
      </div>
      <p className="muted small">
        A version is a full copy of this story that players choose on this page: another language, a
        different reading level, a director's cut. Each version is edited on its own.
      </p>
      {showForm && (
        <NewVersionForm
          story={story}
          versions={versions}
          busy={mut.create.isPending}
          error={mut.create.error}
          onSubmit={async (body) => {
            await mut.create.mutateAsync(body);
            setShowForm(false);
          }}
        />
      )}
      {versionsQ.isError && <ErrorPanel error={versionsQ.error} />}
      {versions.length === 0 ? (
        <EmptyState title="No versions yet.">Players see only the original until you add one.</EmptyState>
      ) : (
        <ul className="version-list">
          {versions.map((v) => (
            <VersionRow
              key={v.id}
              version={v}
              busy={mut.update.isPending || mut.remove.isPending || mut.retry.isPending}
              onToggle={(enabled) => mut.update.mutate({ versionId: v.id, body: { enabled } })}
              onRename={(title) => mut.update.mutate({ versionId: v.id, body: { title } })}
              onRetry={() => mut.retry.mutate(v.id)}
              onDelete={() => mut.remove.mutate(v.id)}
            />
          ))}
        </ul>
      )}
      <MutationError error={mut.update.error ?? mut.remove.error ?? mut.retry.error} />
    </section>
  );
}

function NewVersionForm({
  story,
  versions,
  busy,
  error,
  onSubmit,
}: {
  story: AuthorStory;
  versions: StoryVersion[];
  busy: boolean;
  error: unknown;
  onSubmit: (body: CreateVersionBody) => Promise<void>;
}) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [language, setLanguage] = useState("");
  const [sourceStoryId, setSourceStoryId] = useState(story.id);
  const [transform, setTransform] = useState("");
  const [learningOn, setLearningOn] = useState(false);
  const [gradeLevel, setGradeLevel] = useState("");
  const [coachTone, setCoachTone] = useState("");
  const [rules, setRules] = useState<WritingRules>(DEFAULT_WRITING_RULES);
  const [genPassage, setGenPassage] = useState(3);
  const [genWoven, setGenWoven] = useState(2);
  const [genTarget, setGenTarget] = useState(1);

  function clamp(n: number) {
    return Number.isFinite(n) ? Math.min(10, Math.max(0, Math.round(n))) : 0;
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    const body: CreateVersionBody = { title: title.trim() };
    if (description.trim()) body.description = description.trim();
    if (language.trim()) body.language = language.trim();
    if (sourceStoryId !== story.id) body.sourceStoryId = sourceStoryId;
    if (transform.trim()) body.transform = transform.trim();
    if (learningOn) {
      const learning: VersionLearningSetup = {
        enabled: true,
        gradeLevel: gradeLevel.trim() || null,
        coachTone: coachTone.trim() || null,
        writingRules: rules,
      };
      const counts = { passagePerChapter: clamp(genPassage), wovenPerChapter: clamp(genWoven), targetPerChapter: clamp(genTarget) };
      if (counts.passagePerChapter + counts.wovenPerChapter + counts.targetPerChapter > 0) learning.generate = counts;
      body.learning = learning;
    }
    try {
      await onSubmit(body);
      setTitle("");
      setDescription("");
      setLanguage("");
      setTransform("");
      setSourceStoryId(story.id);
      setLearningOn(false);
      setGradeLevel("");
      setCoachTone("");
      setRules(DEFAULT_WRITING_RULES);
      setGenPassage(3);
      setGenWoven(2);
      setGenTarget(1);
    } catch {
      /* rendered below */
    }
  }

  const generating = learningOn && clamp(genPassage) + clamp(genWoven) + clamp(genTarget) > 0;

  const readySources = versions.filter((v) => v.status === "ready");

  return (
    <form className="card version-form" onSubmit={submit}>
      <div className="form-grid">
        <label className="field">
          <span>Title</span>
          <input
            type="text"
            required
            maxLength={80}
            placeholder="Spanish, Grade 8, Director's cut…"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </label>
        <label className="field">
          <span>Language (optional)</span>
          <input
            type="text"
            maxLength={16}
            placeholder="es, fr, en-GB"
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
          />
        </label>
      </div>
      <label className="field">
        <span>Description (optional)</span>
        <input
          type="text"
          maxLength={200}
          placeholder="Shown to players in the chooser"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </label>
      <label className="field">
        <span>Copy from</span>
        <select value={sourceStoryId} onChange={(e) => setSourceStoryId(e.target.value)}>
          <option value={story.id}>Original</option>
          {readySources.map((v) => (
            <option key={v.id} value={v.storyId}>
              {v.title}
            </option>
          ))}
        </select>
      </label>
      <label className="field">
        <span>Transform instruction (optional)</span>
        <textarea rows={3} value={transform} onChange={(e) => setTransform(e.target.value)} placeholder={TRANSFORM_HINT} />
        <span className="muted small">{TRANSFORM_HINT}</span>
      </label>

      <div className="version-learning">
        <label className="check">
          <input type="checkbox" checked={learningOn} onChange={(e) => setLearningOn(e.target.checked)} />
          <span>Turn on learning mode for this version</span>
        </label>
        {learningOn && (
          <div className="version-learning-body">
            <div className="form-grid">
              <label className="field">
                <span>Grade level</span>
                <input
                  type="text"
                  maxLength={16}
                  placeholder="4, 9, 12…"
                  value={gradeLevel}
                  onChange={(e) => setGradeLevel(e.target.value)}
                />
              </label>
              <label className="field">
                <span>Coach tone (optional)</span>
                <input
                  type="text"
                  maxLength={120}
                  placeholder="encouraging, specific, brief"
                  value={coachTone}
                  onChange={(e) => setCoachTone(e.target.value)}
                />
              </label>
            </div>
            <div className="field">
              <span>Writing rules</span>
              <WritingRulesEditor value={rules} onChange={setRules} />
            </div>
            <fieldset className="version-generate">
              <legend>Generate vocabulary and target words</legend>
              <div className="version-generate-counts">
                <label className="field field-num">
                  <span>Passage words per chapter</span>
                  <input type="number" min={0} max={10} value={genPassage} onChange={(e) => setGenPassage(Number(e.target.value))} />
                </label>
                <label className="field field-num">
                  <span>Woven words per chapter</span>
                  <input type="number" min={0} max={10} value={genWoven} onChange={(e) => setGenWoven(Number(e.target.value))} />
                </label>
                <label className="field field-num">
                  <span>Target words per chapter</span>
                  <input type="number" min={0} max={10} value={genTarget} onChange={(e) => setGenTarget(Number(e.target.value))} />
                </label>
              </div>
              <span className="muted small">
                Generated from the rewritten text, so words match the reading level. Set all to 0 to add words
                yourself later.
              </span>
            </fieldset>
          </div>
        )}
      </div>

      <MutationError error={error} />
      <div className="form-actions">
        <button type="submit" className="btn btn-primary" disabled={busy || !title.trim()}>
          {busy ? "Creating…" : transform.trim() || generating ? "Create and generate" : "Create version"}
        </button>
      </div>
    </form>
  );
}

function VersionRow({
  version: v,
  busy,
  onToggle,
  onRename,
  onRetry,
  onDelete,
}: {
  version: StoryVersion;
  busy: boolean;
  onToggle: (enabled: boolean) => void;
  onRename: (title: string) => void;
  onRetry: () => void;
  onDelete: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(v.title);
  const p = v.progress;
  const pct = v.status === "generating" && p && p.total > 0 ? Math.round((100 * p.done) / p.total) : null;

  function commit() {
    const t = draft.trim();
    setEditing(false);
    if (t && t !== v.title) onRename(t);
    else setDraft(v.title);
  }

  return (
    <li className={`version-row version-${v.status}`}>
      <div className="version-row-main">
        <div className="version-row-title">
          {editing ? (
            <input
              type="text"
              className="inline-edit"
              value={draft}
              maxLength={80}
              autoFocus
              onChange={(e) => setDraft(e.target.value)}
              onBlur={commit}
              onKeyDown={(e) => {
                if (e.key === "Enter") commit();
                if (e.key === "Escape") {
                  setDraft(v.title);
                  setEditing(false);
                }
              }}
            />
          ) : (
            <button type="button" className="link-button" onClick={() => setEditing(true)} title="Rename">
              {v.title}
            </button>
          )}
          {v.language && <span className="tag">{v.language}</span>}
          {v.learningSetup?.enabled && (
            <span
              className="tag tag-learning"
              title={v.learningSetup.gradeLevel ? `Learning mode · grade ${v.learningSetup.gradeLevel}` : "Learning mode"}
            >
              Learning
            </span>
          )}
          <span className={`badge badge-version-${v.status}${v.enabled ? "" : " badge-muted"}`}>
            {versionStatusLabel(v)}
          </span>
        </div>
        {v.description && <div className="muted small">{v.description}</div>}
        {v.transform && (
          <div className="muted small version-transform" title={v.transform}>
            Transform: {v.transform}
          </div>
        )}
        {pct !== null && (
          <div className="image-progress" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct}>
            <div className="image-progress-bar" style={{ width: `${pct}%` }} />
          </div>
        )}
        {v.status === "failed" && (
          <div className="error small">{p?.error ?? "The transform failed."}</div>
        )}
        {p && p.failed.length > 0 && v.status !== "generating" && (
          <details className="small">
            <summary className="muted">
              {p.failed.length} {p.failed.length === 1 ? "field" : "fields"} could not be rewritten
            </summary>
            <ul className="muted">
              {p.failed.map((f, i) => (
                <li key={i}>{f}</li>
              ))}
            </ul>
          </details>
        )}
      </div>
      <div className="version-row-side">
        <label className="toggle small">
          <input type="checkbox" checked={v.enabled} disabled={busy} onChange={(e) => onToggle(e.target.checked)} />
          Enabled
        </label>
        {(v.status === "failed" || v.status === "generating") && (
          <button type="button" className="btn btn-sm" disabled={busy} onClick={onRetry}>
            Retry
          </button>
        )}
        <Link to={`/stories/${v.storyId}/map`} className="btn btn-sm">
          Open
        </Link>
        <InlineConfirm
          label="Delete"
          question={`Delete version "${v.title}" and everything in it?`}
          confirmLabel="Delete"
          busy={busy}
          onConfirm={onDelete}
        />
      </div>
    </li>
  );
}

// ---- Chapters (author) ----------------------------------------------------------------

function ChaptersSection({ story }: { story: AuthorStory }) {
  const createChapter = useCreateChapter(story.id);
  const reorder = useReorderChapters(story.id);
  const chapters = [...story.chapters].sort((a, b) => a.sortOrder - b.sortOrder);
  const busy = reorder.isPending || createChapter.isPending;

  function move(index: number, delta: number) {
    const target = index + delta;
    if (target < 0 || target >= chapters.length) return;
    const ids = chapters.map((c) => c.id);
    const [moved] = ids.splice(index, 1);
    ids.splice(target, 0, moved!);
    reorder.mutate(ids);
  }

  return (
    <section className="section">
      <div className="section-head">
        <h2>Chapters</h2>
        <button
          type="button"
          className="btn btn-sm btn-primary"
          onClick={() => createChapter.mutate(undefined)}
          disabled={busy}
        >
          {createChapter.isPending ? "Adding…" : "Add chapter"}
        </button>
      </div>
      <MutationError error={createChapter.error ?? reorder.error} />
      {chapters.length === 0 ? (
        <EmptyState title="No chapters yet">
          Add a chapter to start building. The first chapter you add becomes the starting
          chapter.
        </EmptyState>
      ) : (
        <ol className="chapter-list">
          {chapters.map((c, i) => (
            <li key={c.id} className="chapter-row">
              <div className="chapter-row-order muted small">{i + 1}</div>
              <div className="chapter-row-main">
                <Link to={`/stories/${story.id}/chapters/${c.id}`} className="chapter-row-title">
                  {c.title}
                </Link>
                <div className="muted small">
                  {c.sceneCount} {c.sceneCount === 1 ? "scene" : "scenes"}
                </div>
              </div>
              <div className="chapter-row-side">
                {story.startChapterId === c.id && <Badge tone="accent">Start</Badge>}
                {c.isEnding && <Badge tone="muted">Ending</Badge>}
                <button
                  type="button"
                  className="btn btn-sm btn-icon"
                  aria-label="Move up"
                  title="Move up"
                  disabled={busy || i === 0}
                  onClick={() => move(i, -1)}
                >
                  ↑
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-icon"
                  aria-label="Move down"
                  title="Move down"
                  disabled={busy || i === chapters.length - 1}
                  onClick={() => move(i, 1)}
                >
                  ↓
                </button>
              </div>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
